import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { API_CONFIG } from './config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './pages/auth/Login';
import UserManagement from './pages/auth/UserManagement';
import Header from './components/Header';
import { Funnel } from '@phosphor-icons/react';
import ExternalImport from './pages/ExternalImport';
import BlocklistImport from './pages/BlocklistImport';
import BlocklistOverview from './pages/BlocklistOverview';
import Datenpflege from './pages/Datenpflege';
import Abgleich from './pages/Abgleich';
import Auswahl from './pages/Auswahl';
import Einsatzplanung from './pages/Einsatzplanung';
import KsPlanung from './pages/KsPlanung';
import KsWunschplan from './pages/KsWunschplan';
import DialfireSyncPage from './pages/DialfireSync';
import OptinCache from './pages/OptinCache';
import WpoolView from './pages/WpoolView';
import MubView from './pages/MubView';
import MubSelektion from './pages/MubSelektion';
import AbuntisReview from './pages/AbuntisReview';
import Auswertungen from './pages/Auswertungen';
import DeduplicationCampaignManager from './components/DeduplicationCampaignManager';
import CampaignManager from './components/CampaignManager';
import DocumentManagement from './pages/DocumentManagement';
import WorkflowGuide from './pages/WorkflowGuide';
import SperrlisteView from './pages/SperrlisteView';
// WKZOverview removed - functionality available in Actions tab
import { User, Crown, Users, ArrowsClockwise, ProhibitInset, File, Megaphone, ChartBar, ListChecks, Trash, ArrowUUpLeft, Phone, AddressBook, Database, Table } from '@phosphor-icons/react';

interface ServiceStatus {
  name: string;
  url: string;
  status: 'online' | 'offline' | 'checking';
}

function Dashboard() {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [services, setServices] = useState<ServiceStatus[]>([
    { name: 'API Service', url: API_CONFIG.HEALTH.API, status: 'checking' },
    { name: 'Auth Service', url: API_CONFIG.HEALTH.AUTH, status: 'checking' },
    { name: 'Ingest Service', url: API_CONFIG.HEALTH.INGEST, status: 'checking' },
    { name: 'Dedup Service', url: API_CONFIG.HEALTH.DEDUP, status: 'checking' },
    { name: 'RabbitMQ Management', url: API_CONFIG.HEALTH.RABBITMQ, status: 'checking' }
  ]);

  const checkServiceStatus = async (service: ServiceStatus): Promise<'online' | 'offline'> => {
    try {
      await fetch(service.url, { method: 'GET', mode: 'no-cors' });
      return 'online';
    } catch (error) {
      return 'offline';
    }
  };

  useEffect(() => {
    const checkAllServices = async () => {
      const updatedServices: ServiceStatus[] = await Promise.all(
        services.map(async (service): Promise<ServiceStatus> => ({
          ...service,
          status: await checkServiceStatus(service)
        }))
      );
      setServices(updatedServices);
    };

    checkAllServices();
    const interval = setInterval(checkAllServices, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <Header />

      {/* User Info Bar */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: isMobile ? '0.75rem 1rem' : '0.75rem 2rem',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'flex-start' : 'flex-end',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? '0.75rem' : '1rem'
      }}>
        <span style={{
          color: '#6b7280',
          fontSize: isMobile ? '0.85rem' : '0.9rem',
          textAlign: isMobile ? 'left' : 'right',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <User size={16} weight="fill" /> {user?.first_name} {user?.last_name}
          {user?.role === 'admin' && <span style={{
            marginLeft: '8px',
            padding: '2px 8px',
            backgroundColor: '#ffeaa7',
            color: '#d63031',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 600
          }}>Admin</span>}
        </span>
        <button
          onClick={logout}
          style={{
            padding: isMobile ? '0.6rem' : '0.5rem 1rem',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            transition: 'background-color 0.2s',
            width: isMobile ? '100%' : 'auto'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
        >
          Abmelden
        </button>
      </div>

      {/* Admin Quick Access Banner */}
      {user?.role === 'admin' && (
        <div style={{
          backgroundColor: '#ffeaa7',
          borderBottom: '2px solid #fdcb6e',
          padding: isMobile ? '1rem' : '1rem 2rem',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Crown size={isSmallMobile ? 20 : 24} weight="fill" color="#d63031" />
            <div>
              <div style={{ fontWeight: 600, color: '#d63031', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                Admin-Bereich
              </div>
              <div style={{ fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#2d3436' }}>
                {isMobile ? 'Erweiterte Funktionen' : 'Sie haben Zugriff auf erweiterte Funktionen'}
              </div>
            </div>
          </div>
          <Link
            to="/users"
            style={{
              padding: isMobile ? '12px 16px' : '10px 20px',
              backgroundColor: '#d63031',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              textAlign: 'center'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#c0392b';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#d63031';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Users size={18} weight="fill" /> Benutzerverwaltung
          </Link>
        </div>
      )}

      <main style={{
        maxWidth: '1400px',
        margin: isMobile ? '1rem auto' : '2rem auto',
        padding: isMobile ? '0 1rem' : '0 2rem'
      }}>
        {/* Erste Zeile - Hauptkacheln */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : user?.role === 'admin' ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
          gap: isMobile ? '1rem' : '1.5rem',
          marginBottom: isMobile ? '1rem' : '2rem'
        }}>
          {/* Benutzerverwaltung - nur für Admins */}
          {user?.role === 'admin' && (
            <Link to="/users" style={{ textDecoration: 'none', display: 'flex' }}>
              <div style={{
                backgroundColor: 'white',
                padding: isMobile ? '1.5rem' : '2rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                border: '2px solid #ffeaa7',
                transition: 'box-shadow 0.2s',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
              }}
              onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'}
              onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)'}
              >
                <h3 style={{
                  margin: '0 0 0.5rem 0',
                  color: '#d63031',
                  fontSize: isMobile ? '1.1rem' : '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Crown size={20} weight="fill" /> Benutzerverwaltung
                </h3>
                <p style={{
                  margin: 0,
                  color: '#6b7280',
                  fontSize: isMobile ? '0.9rem' : '0.95rem'
                }}>
                  Benutzer & Berechtigungen verwalten
                </p>
              </div>
            </Link>
          )}

          {/* Planung */}
          <Link to="/campaigns" style={{ textDecoration: 'none', display: 'flex' }}>
            <div style={{
              backgroundColor: 'white',
              padding: isMobile ? '1.5rem' : '2rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              border: '2px solid #dbeafe',
              transition: 'box-shadow 0.2s',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}
            onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,102,204,0.2)'}
            onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)'}
            >
              <h3 style={{
                margin: '0 0 0.5rem 0',
                color: '#0066cc',
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Megaphone size={20} weight="fill" /> Planung
              </h3>
              <p style={{
                margin: 0,
                color: '#6b7280',
                fontSize: isMobile ? '0.9rem' : '0.95rem'
              }}>
                Lotterien, Callcenter, Kampagnen & Aktionen verwalten
              </p>
            </div>
          </Link>

          {/* Dokumentation */}
          <Link to="/documents" style={{ textDecoration: 'none', display: 'flex' }}>
            <div style={{
              backgroundColor: 'white',
              padding: isMobile ? '1.5rem' : '2rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              border: '1px solid #e5e7eb',
              transition: 'box-shadow 0.2s',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}
            onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'}
            onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)'}
            >
              <h3 style={{
                margin: '0 0 0.5rem 0',
                color: '#003366',
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <File size={20} weight="fill" /> Dokumentation
              </h3>
              <p style={{
                margin: '0 0 0.5rem 0',
                color: '#6b7280',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                fontWeight: 500
              }}>
                Werbemittel
              </p>
              <p style={{
                margin: 0,
                color: '#9ca3af',
                fontSize: isMobile ? '0.8rem' : '0.85rem'
              }}>
                Speicherung von Dokumentationen & Gesprächsleitfäden
              </p>
            </div>
          </Link>

          {/* Controlling */}
          <Link to="/auswertungen" style={{ textDecoration: 'none', display: 'flex' }}>
            <div style={{
              backgroundColor: 'white',
              padding: isMobile ? '1.5rem' : '2rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              border: '1px solid #e5e7eb',
              transition: 'box-shadow 0.2s',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}
            onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'}
            onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)'}
            >
              <h3 style={{
                margin: '0 0 0.5rem 0',
                color: '#003366',
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <ChartBar size={20} weight="fill" /> Controlling
              </h3>
              <p style={{
                margin: '0 0 0.5rem 0',
                color: '#6b7280',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                fontWeight: 500
              }}>
                Auswertung
              </p>
              <p style={{
                margin: 0,
                color: '#9ca3af',
                fontSize: isMobile ? '0.8rem' : '0.85rem'
              }}>
                AdHoc Berichte & Statistiken
              </p>
            </div>
          </Link>
        </div>

        {/* Zweite Zeile - Große Kacheln */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: isMobile ? '1rem' : '1.5rem',
          marginBottom: isMobile ? '1rem' : '2rem'
        }}>
          {/* Datenpflege */}
          <div style={{
            backgroundColor: 'white',
            padding: isMobile ? '1.5rem' : '2rem',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            border: '2px solid #fee2e2',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{
              margin: '0 0 0.5rem 0',
              color: '#dc2626',
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 600
            }}>
              <ProhibitInset size={24} weight="fill" /> Datenpflege
            </h3>
            <p style={{
              margin: '0 0 1.5rem 0',
              color: '#6b7280',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              fontWeight: 500
            }}>
              Löschungen, Sperrlisten & Widerrufe
            </p>

            {/* Sub-Items */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {/* Datenpflege - Hauptlink */}
              <Link to="/datenpflege" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#fef2f2',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #fecaca',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef2f2';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      gap: '0.5rem'
                    }}>
                      <Trash size={18} weight="fill" color="#dc2626" />
                      <ProhibitInset size={18} weight="fill" color="#ea580c" />
                      <ArrowUUpLeft size={18} weight="fill" color="#7c3aed" />
                    </div>
                  </div>
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#dc2626',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    fontWeight: 600
                  }}>
                    Datenpflege starten
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    Löschung, Sperrliste, Widerruf verarbeiten
                  </p>
                </div>
              </Link>

              {/* Sperrliste */}
              <Link to="/sperrliste" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#fef2f2',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #fecaca',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef2f2';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#ea580c',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: 600
                  }}>
                    <ProhibitInset size={18} weight="fill" /> Zentrale Sperrliste
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: '0.85rem'
                  }}>
                    Blacklist & Böse Buben verwalten, durchsuchen, importieren
                  </p>
                </div>
              </Link>
            </div>
          </div>

          {/* Mailing Management */}
          <div style={{
            backgroundColor: 'white',
            padding: isMobile ? '1.5rem' : '2rem',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{
              margin: '0 0 1.5rem 0',
              color: '#003366',
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 600
            }}>
              <Megaphone size={24} weight="fill" /> Mailing Management
            </h3>

            {/* Sub-Items */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {/* Abgleich */}
              <Link to="/abgleich" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#f9fafb',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#003366',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <ArrowsClockwise size={18} weight="fill" /> Abgleich
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    Neue Adressen gegen Sperrlisten prüfen & in WPool übernehmen
                  </p>
                </div>
              </Link>

              {/* Auswahl */}
              <Link to="/auswahl" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#f9fafb',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#003366',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <ListChecks size={18} weight="fill" /> Auswahl
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    Adressen aus WPool für Mailings auswählen & exportieren
                  </p>
                </div>
              </Link>

              {/* Einsatzplanung */}
              <Link to="/einsatzplanung" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#fef3c7',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #fcd34d',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fde68a'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#92400e',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <ChartBar size={18} weight="fill" /> Einsatzplanung
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    KW-basierte Adress-Einsatzplanung (Vorschlag RV)
                  </p>
                </div>
              </Link>

              {/* Planung KS */}
              <Link to="/ks-planung" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#dbeafe',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #93c5fd',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#bfdbfe'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#1e40af',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Table size={18} weight="fill" /> Planung KS
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    KW-basierte Auflagen-/Zielmengen-Planung
                  </p>
                </div>
              </Link>

              {/* Wunschplan KS */}
              <Link to="/ks-wunschplan" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#ede9fe',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #c4b5fd',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ddd6fe'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ede9fe'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#6d28d9',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <ChartBar size={18} weight="fill" /> Wunschplan KS
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    Abstrahierter Kampagnen-Wunschplan (Soll)
                  </p>
                </div>
              </Link>

              {/* Dialfire OptIn Sync */}
              <Link to="/dialfire" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#ede9fe',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #c4b5fd',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ddd6fe'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ede9fe'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#5b21b6',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Phone size={18} weight="fill" /> Dialfire OptIn
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    OptIn/OptOut Daten aus Dialfire Postgres synchronisieren
                  </p>
                </div>
              </Link>

              {/* OptIn Cache */}
              <Link to="/optin-cache" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#ecfdf5',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #a7f3d0',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ecfdf5'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#065f46',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <AddressBook size={18} weight="fill" /> OptIn Cache
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    Importierte OptIn-Daten durchsuchen & filtern
                  </p>
                </div>
              </Link>

              {/* WPool Daten */}
              <Link to="/wpool" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#eff6ff',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #bfdbfe',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#1e40af',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Database size={18} weight="fill" /> WPool Daten
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    Mailing-Adressen durchsuchen (566k Datens&auml;tze)
                  </p>
                </div>
              </Link>

              {/* MUB Daten */}
              <Link to="/mub" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#f5f3ff',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #c4b5fd',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ede9fe'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f5f3ff'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#5b21b6',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Users size={18} weight="fill" /> MUB Daten
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    Spielteilnehmer (BOH/BOK) durchsuchen & filtern
                  </p>
                </div>
              </Link>

              {/* MUB Selektion */}
              <Link to="/mub-selektion" style={{ textDecoration: 'none' }}>
                <div style={{
                  backgroundColor: '#faf5ff',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #d8b4fe',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3e8ff'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#faf5ff'}
                >
                  <h4 style={{
                    margin: '0 0 0.25rem 0',
                    color: '#7c3aed',
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Funnel size={18} weight="fill" /> MuB Selektion
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.8rem' : '0.85rem'
                  }}>
                    Konfigurierbare Exporte aus MuB + OptIn
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '1.5rem' : '2rem',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          border: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            margin: '0 0 1.5rem 0',
            color: '#003366',
            fontSize: isMobile ? '1.25rem' : '1.5rem',
            fontWeight: '600'
          }}>
            System Status
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            {services.map((service) => (
              <div key={service.name} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: isMobile ? '0.85rem' : '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: service.status === 'online' ? '#10b981' : service.status === 'offline' ? '#ef4444' : '#f59e0b',
                  flexShrink: 0
                }}></div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontWeight: '500',
                    color: '#111827',
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {service.name}
                  </div>
                  <div style={{ fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#6b7280' }}>
                    {service.status === 'online' ? 'Online' : service.status === 'offline' ? 'Offline' : 'Checking...'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '1.5rem' : '2rem',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          border: '1px solid #e5e7eb',
          marginTop: isMobile ? '1rem' : '2rem'
        }}>
          <h3 style={{
            margin: '0 0 1rem 0',
            color: '#003366',
            fontSize: isMobile ? '1.1rem' : '1.25rem',
            fontWeight: '600'
          }}>
            Quick Links
          </h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <a href={`${API_CONFIG.API_V1}/campaigns`} target="_blank" rel="noopener noreferrer"
              style={{
                padding: isMobile ? '0.6rem 0.9rem' : '0.5rem 1rem',
                backgroundColor: '#003366',
                color: 'white',
                borderRadius: '4px',
                textDecoration: 'none',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                whiteSpace: 'nowrap'
              }}>
              Kampagnen API
            </a>
            <a href={API_CONFIG.HEALTH.AUTH} target="_blank" rel="noopener noreferrer"
              style={{
                padding: isMobile ? '0.6rem 0.9rem' : '0.5rem 1rem',
                backgroundColor: '#003366',
                color: 'white',
                borderRadius: '4px',
                textDecoration: 'none',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                whiteSpace: 'nowrap'
              }}>
              Auth Service
            </a>
            <a href={API_CONFIG.HEALTH.DEDUP} target="_blank" rel="noopener noreferrer"
              style={{
                padding: isMobile ? '0.6rem 0.9rem' : '0.5rem 1rem',
                backgroundColor: '#003366',
                color: 'white',
                borderRadius: '4px',
                textDecoration: 'none',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                whiteSpace: 'nowrap'
              }}>
              Dedup Service
            </a>
            <a href={API_CONFIG.RABBITMQ_URL} target="_blank" rel="noopener noreferrer"
              style={{
                padding: isMobile ? '0.6rem 0.9rem' : '0.5rem 1rem',
                backgroundColor: '#003366',
                color: 'white',
                borderRadius: '4px',
                textDecoration: 'none',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                whiteSpace: 'nowrap'
              }}>
              RabbitMQ Management
            </a>
          </div>
        </div>
      </main>

      <footer style={{
        backgroundColor: '#003366',
        color: 'white',
        padding: isMobile ? '1.25rem 0' : '1.5rem 0',
        marginTop: isMobile ? '2rem' : '4rem',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '0 1rem' : '0 2rem'
        }}>
          <p style={{
            margin: 0,
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            opacity: 0.8
          }}>
            CRM Boesche
          </p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Route */}
          <Route path="/login" element={<Login />} />

          {/* Protected Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/external-import" element={
            <ProtectedRoute>
              <ExternalImport />
            </ProtectedRoute>
          } />
          <Route path="/abgleich" element={
            <ProtectedRoute>
              <Abgleich />
            </ProtectedRoute>
          } />
          <Route path="/auswahl" element={
            <ProtectedRoute>
              <Auswahl />
            </ProtectedRoute>
          } />
          <Route path="/einsatzplanung" element={
            <ProtectedRoute>
              <Einsatzplanung />
            </ProtectedRoute>
          } />
          <Route path="/ks-planung" element={
            <ProtectedRoute>
              <KsPlanung />
            </ProtectedRoute>
          } />
          <Route path="/ks-wunschplan" element={
            <ProtectedRoute>
              <KsWunschplan />
            </ProtectedRoute>
          } />
          <Route path="/dialfire" element={
            <ProtectedRoute>
              <DialfireSyncPage />
            </ProtectedRoute>
          } />
          <Route path="/abuntis-review/:jobId" element={
            <ProtectedRoute>
              <AbuntisReview />
            </ProtectedRoute>
          } />
          <Route path="/optin-cache" element={
            <ProtectedRoute>
              <OptinCache />
            </ProtectedRoute>
          } />
          <Route path="/wpool" element={
            <ProtectedRoute>
              <WpoolView />
            </ProtectedRoute>
          } />
          <Route path="/mub" element={
            <ProtectedRoute>
              <MubView />
            </ProtectedRoute>
          } />
          <Route path="/mub-selektion" element={
            <ProtectedRoute>
              <MubSelektion />
            </ProtectedRoute>
          } />
          <Route path="/auswertungen" element={
            <ProtectedRoute>
              <Auswertungen />
            </ProtectedRoute>
          } />
          <Route path="/blocklist-import" element={
            <ProtectedRoute>
              <BlocklistImport />
            </ProtectedRoute>
          } />
          <Route path="/blocklist-overview" element={
            <ProtectedRoute>
              <BlocklistOverview />
            </ProtectedRoute>
          } />
          <Route path="/datenpflege" element={
            <ProtectedRoute>
              <Datenpflege />
            </ProtectedRoute>
          } />
          <Route path="/dedup" element={
            <ProtectedRoute>
              <DeduplicationCampaignManager />
            </ProtectedRoute>
          } />
          <Route path="/campaigns" element={
            <ProtectedRoute>
              <CampaignManager />
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute>
              <DocumentManagement />
            </ProtectedRoute>
          } />
          <Route path="/workflow" element={
            <ProtectedRoute>
              <WorkflowGuide />
            </ProtectedRoute>
          } />
          <Route path="/sperrliste" element={
            <ProtectedRoute>
              <SperrlisteView />
            </ProtectedRoute>
          } />
          {/* WKZ-Overview removed - functionality available in Actions tab */}

          {/* Admin Only Routes */}
          <Route path="/users" element={
            <ProtectedRoute requireAdmin={true}>
              <UserManagement />
            </ProtectedRoute>
          } />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
